import React, { useState, useMemo } from "react";
import { Minus, Plus, Check, Gift, ShieldCheck, CreditCard } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { useToast } from "../hooks/use-toast";
import { cities, cityLabel, HOURLY_RATE, weekdaysByLang } from "../mock";
import { useLang } from "../context/LanguageContext";
import { createBooking } from "../api";
import { Link } from "./L";

const fmt = (n) => n.toFixed(2);

const BookingForm = ({ defaultCity = "", title }) => {
  const { lang, t } = useLang();
  const b = t.booking;
  const { toast } = useToast();
  const weekdays = weekdaysByLang[lang];

  const [city, setCity] = useState(defaultCity);
  const [hours, setHours] = useState(3);
  const [freq, setFreq] = useState(b.frequencies[1]);
  const [days, setDays] = useState([]);
  const TIME_WINDOWS = lang === "de" ? ["Vormittag (8–12 Uhr)", "Nachmittag (13–17 Uhr)", "Flexibel"] : ["Morning (8am–12pm)", "Afternoon (1–5pm)", "Flexible"];
  const [timeWindow, setTimeWindow] = useState("");
  const [startDate, setStartDate] = useState("");
  const minDate = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
  const [selTasks, setSelTasks] = useState(["staubsaugen", "wischen", "bad"]);
  const [clang, setClang] = useState(b.langs[0]);
  const [neukunde, setNeukunde] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [pets, setPets] = useState(false);
  const [laundry, setLaundry] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "" });

  const toggle = (arr, setArr, val) => setArr(arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]);

  const { gross, discount, total } = useMemo(() => {
    const g = hours * HOURLY_RATE;
    const d = neukunde ? HOURLY_RATE : 0;
    return { gross: g, discount: d, total: Math.max(g - d, 0) };
  }, [hours, neukunde]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!city) { toast({ title: b.errCity, variant: "destructive" }); return; }
    if (!form.name || !form.email) { toast({ title: b.errName, variant: "destructive" }); return; }
    setSubmitting(true);
    try {
      await createBooking({ city, hours, freq, days, time_window: timeWindow, start_date: startDate, tasks: selTasks, clang, neukunde, pets, laundry, ...form });
      toast({ title: b.okTitle, description: b.okDesc });
      setForm({ name: "", email: "", phone: "", address: "" });
    } catch (err) {
      toast({ title: b.errName, description: "", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden">
      <div className="bg-teal-700 px-6 py-5 text-white">
        <h3 className="font-head font-bold text-xl">{title || b.title}</h3>
        <p className="text-teal-100 text-sm mt-1">{b.header}</p>
      </div>

      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">{b.cityLabel}</label>
          <Select value={city} onValueChange={setCity}>
            <SelectTrigger className="h-12 rounded-xl border-slate-200"><SelectValue placeholder={b.cityPlaceholder} /></SelectTrigger>
            <SelectContent>
              {cities.map((c) => (<SelectItem key={c.slug} value={c.slug}>{cityLabel(c, lang)}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">{b.hoursLabel}</label>
            <div className="flex items-center justify-between h-12 rounded-xl border border-slate-200 px-2">
              <button type="button" onClick={() => setHours(Math.max(2, hours - 1))} className="w-9 h-9 rounded-lg bg-teal-50 text-teal-700 flex items-center justify-center hover:bg-teal-100"><Minus className="w-4 h-4" /></button>
              <span className="font-head font-bold text-lg text-slate-900">{hours} {b.hoursUnit}</span>
              <button type="button" onClick={() => setHours(Math.min(40, hours + 1))} className="w-9 h-9 rounded-lg bg-teal-50 text-teal-700 flex items-center justify-center hover:bg-teal-100"><Plus className="w-4 h-4" /></button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">{b.freqLabel}</label>
            <Select value={freq} onValueChange={setFreq}>
              <SelectTrigger className="h-12 rounded-xl border-slate-200"><SelectValue /></SelectTrigger>
              <SelectContent>
                {b.frequencies.map((f) => (<SelectItem key={f} value={f}>{f}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">{b.daysLabel}</label>
          <div className="flex flex-wrap gap-2">
            {weekdays.map((d) => (
              <button key={d} type="button" onClick={() => toggle(days, setDays, d)}
                className={`min-w-[52px] h-11 px-2 rounded-xl text-sm font-semibold transition-colors ${days.includes(d) ? "bg-teal-700 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                {d}
              </button>
            ))}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">{lang === "de" ? "Welche Zeit am liebsten?" : "Preferred time?"}</label>
            <div className="flex flex-col gap-2">
              {TIME_WINDOWS.map((tw, i) => (
                <button key={tw} type="button" data-testid={`booking-time-${i}`} onClick={() => setTimeWindow(timeWindow === tw ? "" : tw)}
                  className={`h-11 px-3 rounded-xl text-sm font-semibold text-left transition-colors ${timeWindow === tw ? "bg-teal-700 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                  {tw}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label htmlFor="booking-start-date" className="block text-sm font-semibold text-slate-700 mb-2">{lang === "de" ? "Ab wann?" : "Start from?"}</label>
            <input id="booking-start-date" data-testid="booking-start-date" type="date" min={minDate} value={startDate} onChange={(e) => setStartDate(e.target.value)}
              className="w-full h-12 rounded-xl border border-slate-200 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/30" />
            <p className="text-xs text-slate-400 mt-1.5">{lang === "de" ? "Optional – wir richten uns möglichst danach." : "Optional – we'll do our best to match it."}</p>
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">{b.tasksLabel}</label>
          <div className="grid grid-cols-2 gap-2">
            {t.tasks.map((tk) => {
              const active = selTasks.includes(tk.id);
              return (
                <button key={tk.id} type="button" onClick={() => toggle(selTasks, setSelTasks, tk.id)}
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm text-left transition-colors border ${active ? "bg-teal-50 border-teal-600 text-teal-800" : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                  <span className={`w-5 h-5 rounded-md flex items-center justify-center shrink-0 ${active ? "bg-teal-600" : "bg-slate-100"}`}>
                    {active && <Check className="w-3.5 h-3.5 text-white" />}
                  </span>
                  {tk.label}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">{b.langLabel}</label>
          <div className="flex gap-2">
            {b.langs.map((l) => (
              <button key={l} type="button" onClick={() => setClang(l)}
                className={`flex-1 h-11 rounded-xl text-sm font-semibold transition-colors ${clang === l ? "bg-teal-700 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                {l}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">{b.petsLabel}</label>
          <div className="flex gap-2 max-w-[240px]">
            <button type="button" data-testid="booking-pets-no" onClick={() => setPets(false)} className={`flex-1 h-12 rounded-xl text-sm font-semibold transition-colors ${!pets ? "bg-teal-700 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{b.petsNo}</button>
            <button type="button" data-testid="booking-pets-yes" onClick={() => setPets(true)} className={`flex-1 h-12 rounded-xl text-sm font-semibold transition-colors ${pets ? "bg-teal-700 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{b.petsYes}</button>
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">{b.laundryLabel}</label>
          <div className="flex gap-2 max-w-[240px]">
            <button type="button" data-testid="booking-laundry-no" onClick={() => setLaundry(false)} className={`flex-1 h-12 rounded-xl text-sm font-semibold transition-colors ${!laundry ? "bg-teal-700 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{b.petsNo}</button>
            <button type="button" data-testid="booking-laundry-yes" onClick={() => setLaundry(true)} className={`flex-1 h-12 rounded-xl text-sm font-semibold transition-colors ${laundry ? "bg-teal-700 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{b.petsYes}</button>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={b.name} className="h-12 rounded-xl border border-slate-200 px-4 text-[15px] focus:outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-100" />
          <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={b.phone} className="h-12 rounded-xl border border-slate-200 px-4 text-[15px] focus:outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-100" />
          <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} type="email" placeholder={b.email} className="h-12 rounded-xl border border-slate-200 px-4 text-[15px] focus:outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-100" />
        </div>
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1.5">{b.addressLabel}</label>
          <input data-testid="booking-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder={b.addressPlaceholder} className="w-full h-12 rounded-xl border border-slate-200 px-4 text-[15px] focus:outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-100" />
        </div>

        <button type="button" onClick={() => setNeukunde(!neukunde)} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border text-left transition-colors ${neukunde ? "bg-amber-50 border-amber-300" : "bg-white border-slate-200"}`}>
          <span className={`w-5 h-5 rounded-md flex items-center justify-center shrink-0 ${neukunde ? "bg-amber-500" : "bg-slate-100"}`}>{neukunde && <Check className="w-3.5 h-3.5 text-white" />}</span>
          <Gift className="w-5 h-5 text-amber-500" />
          <span className="text-sm font-medium text-slate-700">{b.neukunde}</span>
        </button>

        <div className="rounded-xl bg-slate-50 p-4">
          <div className="flex items-center justify-between text-sm text-slate-600">
            <span>{hours} {b.hoursUnit} × 43.90 CHF</span><span>{fmt(gross)} CHF</span>
          </div>
          {discount > 0 && (
            <div className="flex items-center justify-between text-sm text-amber-600 font-medium mt-1">
              <span>{b.discount}</span><span>− {fmt(discount)} CHF</span>
            </div>
          )}
          <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-200">
            <span className="font-head font-bold text-slate-900">{b.perEinsatz}</span>
            <span className="font-head font-extrabold text-2xl text-teal-700">{fmt(total)} CHF</span>
          </div>
        </div>

        <button type="submit" disabled={submitting} className="w-full h-14 rounded-xl bg-teal-700 text-white font-head font-bold text-lg hover:bg-teal-800 transition-colors shadow-lg shadow-teal-800/20 disabled:opacity-60">
          {submitting ? "…" : b.submit}
        </button>

        <div data-testid="booking-nonbinding-note" className="bg-teal-50 border border-teal-100 text-teal-900 rounded-xl px-4 py-3 text-sm leading-relaxed">
          {lang === "de"
            ? "Ihre Buchung ist unverbindlich. Wir prüfen die Verfügbarkeit einer passenden Reinigungskraft und bestätigen Ihnen den Auftrag."
            : "Your booking is non-binding. We check the availability of a suitable cleaner and confirm your order."}
        </div>

        <p data-testid="booking-agb-hint" className="text-xs text-slate-500 text-center -mt-2">
          {lang === "de" ? "Mit der Buchung akzeptieren Sie unsere " : "By booking you accept our "}
          <Link to="/agb" className="text-teal-700 underline">{lang === "de" ? "AGB" : "Terms"}</Link>
          {lang === "de" ? " und die " : " and the "}
          <Link to="/datenschutz" className="text-teal-700 underline">{lang === "de" ? "Datenschutzerklärung" : "Privacy Policy"}</Link>.
        </p>

        <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-slate-500">
          <span className="flex items-center gap-1.5"><CreditCard className="w-4 h-4 text-teal-600" /> {b.chipNoCard}</span>
          <span className="flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-teal-600" /> {b.chipInsured}</span>
          <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-teal-600" /> {b.chipMonth}</span>
        </div>
      </form>
    </div>
  );
};

export default BookingForm;
